import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type { CrmDataProvider } from "../providers/types";
import type { QuoterUnitDetail, Sale, Unit } from "../types";
import {
  QUOTER_ADMIN_QUERY_KEY,
  useRefreshQuoterAdmin,
} from "./quoterAdminData";
import { formatWhen } from "./quoterFormat";

/** Rows per page: a project can hold ~10K units, only one page is rendered. */
const PAGE_SIZE = 25;
const FETCH_ALL = { page: 1, perPage: 10_000 };

type View = "pending" | "verified";
type Row = { unit: Unit; detail: QuoterUnitDetail };

/**
 * Almikia Epic EQ2 (TASK-099): unit verification, Direccion only. Lists the
 * units of a project that are still "por verificar" (the quoter warns when one
 * is quoted) and lets Direccion confirm them one by one or in bulk; a confirmed
 * unit shows who verified it and when. A blocked unit carries a semaforo with
 * icon and text.
 */
export const UnitVerificationList = ({
  projectId,
}: {
  projectId: Identifier;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefreshQuoterAdmin();
  const [view, setView] = useState<View>("pending");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<Identifier>>(new Set());
  const [busy, setBusy] = useState(false);

  const rows = useQuery({
    queryKey: [QUOTER_ADMIN_QUERY_KEY, "verification", projectId, view],
    queryFn: async (): Promise<{ rows: Row[]; verifiers: Sale[] }> => {
      const [units, details] = await Promise.all([
        dataProvider.getList<Unit>("units", {
          pagination: FETCH_ALL,
          sort: { field: "identifier", order: "ASC" },
          filter: { project_id: projectId },
        }),
        dataProvider.getList<QuoterUnitDetail>("quoter_unit_details", {
          pagination: FETCH_ALL,
          sort: { field: "unit_id", order: "ASC" },
          filter: { needs_verification: view === "pending" },
        }),
      ]);
      const unitById = new Map(units.data.map((unit) => [unit.id, unit]));
      const joined = details.data
        .filter((detail) => view === "pending" || detail.verified_at != null)
        .flatMap((detail) => {
          const unit = unitById.get(detail.unit_id);
          return unit ? [{ unit, detail }] : [];
        })
        .sort((a, b) =>
          a.unit.identifier.localeCompare(b.unit.identifier, "es", {
            numeric: true,
          }),
        );
      const verifierIds = [
        ...new Set(
          joined.flatMap(({ detail }) =>
            detail.verified_by == null ? [] : [detail.verified_by],
          ),
        ),
      ];
      const verifiers =
        verifierIds.length > 0
          ? (await dataProvider.getMany<Sale>("sales", { ids: verifierIds }))
              .data
          : [];
      return { rows: joined, verifiers };
    },
  });

  const all = rows.data?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil(all.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const visible = all.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  const switchView = (next: View) => {
    setView(next);
    setPage(0);
    setSelected(new Set());
  };
  const toggle = (unitId: Identifier) =>
    setSelected((now) => {
      const next = new Set(now);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      setSelected(new Set());
      await refresh();
      notify("crm.quoter.verify.done");
    } catch {
      notify("crm.quoter.verify.error", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const verifierName = (id: Identifier | null | undefined) => {
    const sale = rows.data?.verifiers.find((row) => row.id === id);
    return sale ? `${sale.first_name} ${sale.last_name}` : "";
  };

  return (
    <section
      className="space-y-3"
      aria-label={translate("crm.quoter.verify.title")}
    >
      <h2 className="text-lg font-semibold">
        {translate("crm.quoter.verify.title")}
      </h2>
      <p className="text-base text-muted-foreground sm:text-sm">
        {translate("crm.quoter.verify.intro")}
      </p>
      <div className="flex flex-wrap gap-2">
        {(["pending", "verified"] as const).map((option) => (
          <Button
            key={option}
            type="button"
            variant={view === option ? "default" : "outline"}
            className="h-11 sm:h-9"
            aria-pressed={view === option}
            onClick={() => switchView(option)}
          >
            {translate(`crm.quoter.verify.view_${option}`)}
          </Button>
        ))}
      </div>

      {view === "pending" && all.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            className="h-11 sm:h-9"
            disabled={busy || selected.size === 0}
            onClick={() =>
              run(() =>
                dataProvider.quoterVerifyUnits(projectId, [...selected]),
              )
            }
          >
            {translate("crm.quoter.verify.bulk", { count: selected.size })}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={() => setSelected(new Set(all.map(({ unit }) => unit.id)))}
          >
            {translate("crm.quoter.verify.select_all", { count: all.length })}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={selected.size === 0}
            onClick={() => setSelected(new Set())}
          >
            {translate("crm.quoter.verify.clear")}
          </Button>
        </div>
      ) : null}

      {rows.isError ? (
        <ListErrorState
          message={translate("crm.quoter.verify.load_error")}
          onRetry={() => rows.refetch()}
        />
      ) : rows.isPending ? (
        <ListCardsSkeleton rows={3} />
      ) : all.length === 0 ? (
        <ListEmptyState
          className="min-h-0 py-4"
          title={translate(`crm.quoter.verify.empty_${view}`)}
          description={translate(`crm.quoter.verify.empty_${view}_hint`)}
        />
      ) : (
        <ul className="space-y-2" data-testid="unit-verification-list">
          {visible.map(({ unit, detail }) => (
            <li key={unit.id}>
              <Card>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="flex items-center gap-3">
                    {view === "pending" ? (
                      <input
                        type="checkbox"
                        className="size-5"
                        aria-label={translate("crm.quoter.verify.select_unit", {
                          unit: unit.identifier,
                        })}
                        checked={selected.has(unit.id)}
                        onChange={() => toggle(unit.id)}
                      />
                    ) : null}
                    <div>
                      <p className="font-medium">{unit.identifier}</p>
                      <p className="text-base text-muted-foreground tabular-nums sm:text-sm">
                        {detail.stage != null
                          ? translate("crm.quoter.verify.stage", {
                              stage: detail.stage,
                            })
                          : translate("crm.quoter.verify.no_stage")}
                        {detail.block_label ? ` · ${detail.block_label}` : ""}
                      </p>
                      {view === "verified" ? (
                        <p className="text-base text-muted-foreground sm:text-sm">
                          {translate("crm.quoter.verify.verified_by_at", {
                            name:
                              verifierName(detail.verified_by) ||
                              translate("crm.quoter.verify.unknown_person"),
                            when: detail.verified_at
                              ? formatWhen(detail.verified_at)
                              : "",
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusPill
                      tone={detail.is_blocked ? "not-ok" : "ok"}
                      text={translate(
                        detail.is_blocked
                          ? "crm.quoter.verify.blocked"
                          : "crm.quoter.verify.not_blocked",
                      )}
                    />
                    {view === "pending" ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-11 sm:h-9"
                        disabled={busy}
                        aria-label={translate(
                          "crm.quoter.verify.confirm_unit",
                          {
                            unit: unit.identifier,
                          },
                        )}
                        onClick={() =>
                          run(() => dataProvider.quoterVerifyUnit(unit.id))
                        }
                      >
                        {translate("crm.quoter.verify.confirm")}
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            {translate("crm.quoter.verify.prev")}
          </Button>
          <span className="text-base tabular-nums sm:text-sm">
            {translate("crm.quoter.verify.page_of", {
              page: current + 1,
              pages: pageCount,
            })}
          </span>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            disabled={current >= pageCount - 1}
            onClick={() => setPage(current + 1)}
          >
            {translate("crm.quoter.verify.next")}
          </Button>
        </div>
      ) : null}
    </section>
  );
};
